import '@caneara/iodine';

// Init
const debug = false;

// Password confirmation rule
Iodine.rule('passwordConfirmation', (value, param) => {
  return (
    value ===
    (document.getElementById(param)
      ? document.getElementById(param).value
      : 'mew')
  );
});
Iodine.setErrorMessage(
  'passwordConfirmation',
  'Password confirmation needs to match password'
);

// Password strength rule — score 3+ when requirements are met (zxcvbn disabled; score set by password-strength.js)
Iodine.rule('passwordStrength', (value) => value >= 3);
Iodine.setErrorMessage(
  'passwordStrength',
  'Password must include uppercase, lowercase, a number, a special character, and be at least 10 characters'
);

// Phone number rule — allow international formats (8–15 digits)
Iodine.rule('phoneNumber', (value) => {
  if (!value) return true;
  if (!/^[\d\s\-().+]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
});
Iodine.setErrorMessage(
  'phoneNumber',
  'Please enter a valid phone number (8–15 digits)'
);

// Postcode rule
Iodine.rule('postcode', (value) => {
  if (!value) return true;
  return /^\d*$/.test(value);
});
Iodine.setErrorMessage('postcode', 'Please enter a valid postcode');

// Required rule
Iodine.setErrorMessage('required', 'This field is required');

// Debug log
function debugLog(message) {
  if (debug) {
    console.log(message);
  }
}

// Filter keyword input delay (debounce)
function delay(callback, ms) {
  let timer = 0;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function () {
      callback.apply(context, args);
    }, ms || 0);
  };
}

// Get the effective value for validation (for radios, use checked value or '')
function getValueForValidation(input, form) {
  if (input.type === 'radio' || input.type === 'checkbox') {
    const checked = form.querySelector(`input[name="${input.name}"]:checked`);
    return checked ? checked.value : '';
  }
  return input.value;
}

// Move the user to the first invalid field after submit validation.
function scrollToFirstError(input) {
  const target = input.closest('.field') ?? input;
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  target.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'center',
  });

  if (typeof input.focus === 'function') {
    input.focus({ preventScroll: true });
  }
}

// Validate the form
export function validateForm(e, form, opts, onSubmit = false) {
  debugLog('Validating form...');

  const submitButtons = form.querySelectorAll('[type="submit"]');
  let isFormValid = true;
  let firstInvalidInput = null;
  const validatedRadioNames = new Set();

  form.querySelectorAll('input, select, textarea').forEach((el) => {
    if (
      el.getAttribute('type') === 'submit' ||
      el.getAttribute('type') === 'button' ||
      el.getAttribute('type') === 'hidden' ||
      // Skip UI-only controls (e.g. "Show password") that share a .field with
      // a real input — otherwise a valid empty-rules pass clears the error.
      !el.name
    ) {
      return;
    }
    // For radios, validate each group only once
    if (el.type === 'radio' && validatedRadioNames.has(el.name)) {
      return;
    }
    if (el.type === 'radio') {
      validatedRadioNames.add(el.name);
    }
    const rules = opts.rules[el.name] ?? [];

    if (onSubmit) el.dataset.touched = 'true';
    const isValid = validateInput(
      el,
      rules,
      form,
      false,
      opts.customValidators
    );
    if (!isValid) {
      isFormValid = false;
      firstInvalidInput = firstInvalidInput ?? el;
    }

    debugLog(`${el.name}: ${isValid}`);
  });

  debugLog('Form validated - is valid: ' + (isFormValid ? 'yes' : 'no'));

  if (!isFormValid) {
    if (e) e.preventDefault();
    if (onSubmit && firstInvalidInput) scrollToFirstError(firstInvalidInput);
    return false;
  } else if (e) {
    form.classList.add('is-valid');
    return true;
  }
}

// Check if one condition [fieldName, expectedValue] is met.
// expectedValue can be true/false (for checkboxes), 'checked'/'unchecked', or any string(s).
function conditionMet(form, fieldName, expectedValue) {
  const el = form.querySelector(`[name="${fieldName}"]`);
  // Missing fields count as empty so requiredIf X '' still applies
  // (e.g. shippingAddressId is unnamed for some guest checkout states).
  if (!el) {
    if (expectedValue === true || expectedValue === 'checked') return false;
    if (expectedValue === false || expectedValue === 'unchecked') return false;
    const expectedList = Array.isArray(expectedValue)
      ? expectedValue
      : [expectedValue];
    return expectedList.some((expected) => String(expected) === '');
  }
  const isCheckbox = el.type === 'checkbox';
  const isRadio = el.type === 'radio';
  let current;
  if (isCheckbox) {
    current = el.checked;
  } else if (isRadio) {
    const checked = form.querySelector(`input[name="${fieldName}"]:checked`);
    current = checked ? checked.value : '';
  } else {
    current = String(el.value).trim();
  }
  const expectedList = Array.isArray(expectedValue)
    ? expectedValue
    : [expectedValue];
  return expectedList.some((expected) => {
    if (isCheckbox) {
      if (expected === true || expected === 'checked') return current === true;
      if (expected === false || expected === 'unchecked')
        return current === false;
    }
    return String(expected) === current;
  });
}

// Resolve rules: replace requiredIf / requiredIfAll / requiredIfAny with "required" when conditions are met.
// - requiredIf: ['requiredIf', 'otherFieldName', expectedValue] or expectedValue array for OR of values.
//   For checkboxes use true/false or 'checked'/'unchecked' (e.g. required when unchecked: false or 'unchecked').
// - requiredIfAll: ['requiredIfAll', [['field1', val1], ['field2', val2], ...]] — required when ALL conditions match.
// - requiredIfAny: ['requiredIfAny', [['field1', val1], ['field2', val2], ...]] — required when ANY condition matches.
function resolveRules(rules, form) {
  const resolved = [];
  for (const rule of rules) {
    if (Array.isArray(rule) && rule[0] === 'requiredIf') {
      const [, otherFieldName, expectedValue] = rule;
      if (conditionMet(form, otherFieldName, expectedValue)) {
        resolved.push('required');
      }
    } else if (Array.isArray(rule) && rule[0] === 'requiredIfAll') {
      const conditions = rule[1];
      if (
        Array.isArray(conditions) &&
        conditions.every(([name, val]) => conditionMet(form, name, val))
      ) {
        resolved.push('required');
      }
    } else if (Array.isArray(rule) && rule[0] === 'requiredIfAny') {
      const conditions = rule[1];
      if (
        Array.isArray(conditions) &&
        conditions.some(([name, val]) => conditionMet(form, name, val))
      ) {
        resolved.push('required');
      }
    } else if (Array.isArray(rule) && rule[0] === 'postcodeIfAu') {
      const [, countryFieldName] = rule;
      if (conditionMet(form, countryFieldName, 'AU')) {
        resolved.push('postcode');
      }
    } else {
      resolved.push(rule);
    }
  }
  return resolved;
}

// Validate an input
export function validateInput(input, rules, form, onChange, customValidators) {
  const isOnChange = onChange ?? false;
  const field = input.closest('.field');
  if (!field) {
    return true;
  }
  const label = field.querySelector('.input-label')
    ? field.querySelector('.input-label').innerHTML
    : 'This field';
  const name = input.getAttribute('name');
  const value = getValueForValidation(input, form);
  const isTouched = input.getAttribute('data-touched') === 'true';

  debugLog(name);
  debugLog(value);
  debugLog(isTouched);
  debugLog(rules);

  const resolvedRules = resolveRules(rules, form);
  let isValid = !resolvedRules.length;

  const result = Iodine.assert(value, resolvedRules);
  isValid = result.valid === true;
  let message = result.error;

  if (customValidators?.[name]) {
    const custom = customValidators[name](value, input, form);
    if (!custom.valid) {
      isValid = false;
      message = custom.message ?? message;
    }
  }

  debugLog(result);

  if (isTouched || (!isTouched && isValid)) {
    let feedback = field.querySelector('.field__message');
    if (!feedback) {
      feedback = document.createElement('p');
      feedback.classList.add('field__message');
      let messages = field.querySelector('.field__messages');
      const controls = input.closest('.field__main');
      if (controls && !messages) {
        messages = document.createElement('div');
        messages.classList.add('field__messages');
        messages.setAttribute('aria-live', 'polite');
        messages.setAttribute('aria-relevant', 'additions removals');
        messages.setAttribute('id', name + 'Errors');
        controls.insertBefore(messages, null);
      }
      if (controls && messages) {
        messages.appendChild(feedback);
      } else if (controls) {
        controls.setAttribute('id', name + 'Errors');
        controls.insertBefore(feedback, null);
      }
    }

    if (isValid) {
      feedback.display = 'none';
      feedback.innerHTML = '';
      feedback.classList.remove('field__message--error');
      // input.setAttribute('aria-describedby', '');
      field.classList.remove('field--has-error');
      field.classList.add('field--has-success');
    } else {
      feedback.display = 'block';
      feedback.innerHTML = message;
      feedback.classList.add('field__message--error');
      // input.setAttribute('aria-describedby', name + 'Message');
      field.classList.add('field--has-error');
      field.classList.remove('field--has-success');
    }
  } else {
    field.classList.remove('field--has-success');
    field.classList.remove('field--has-error');
  }

  input.setAttribute('aria-invalid', String(!isValid));

  return isValid;
}

// Validate the form
export function validate(form, opts) {
  if (form) {
    form.addEventListener('submit', function (e) {
      runFormValidation(e, form, opts);
    });

    const formFields = form
      .querySelectorAll('input, select, textarea, .field__main')
      .forEach((el) => {
        // Skip UI-only controls without a name (e.g. "Show password" toggles)
        if (el.matches('input, select, textarea') && !el.name) {
          return;
        }

        el.addEventListener('change', (e) => {
          el.setAttribute('data-touched', 'true');
          runFieldValidation(el, form, opts);
        });

        el.addEventListener('blur', (e) => {
          el.setAttribute('data-touched', 'true');
          runFieldValidation(el, form, opts);
        });

        el.addEventListener(
          'keydown',
          delay(function (event) {
            runFieldValidation(el, form, opts);
          }, 300)
        );
      });

    // Add 'is-validatable' class to form for styling/JS hooks if not already present
    if (!form.classList.contains('is-validatable')) {
      form.classList.add('is-validatable');
    }

    validateForm(null, form, opts);
  }
}

// Run form validation
function runFormValidation(e, form, opts) {
  validateForm(e, form, opts, true);
}

// Find inputs that have a requiredIf / requiredIfAll / requiredIfAny rule depending on the given field name
function getDependentFields(form, opts, fieldName) {
  const dependents = [];
  const dependsOn = (rule) => {
    if (!Array.isArray(rule)) return false;
    if (rule[0] === 'requiredIf' && rule[1] === fieldName) return true;
    if (rule[0] === 'requiredIfAll' || rule[0] === 'requiredIfAny') {
      const conditions = rule[1];
      return (
        Array.isArray(conditions) &&
        conditions.some(([name]) => name === fieldName)
      );
    }
    return false;
  };
  Object.entries(opts.rules ?? {}).forEach(([name, rules]) => {
    if (rules.some(dependsOn)) {
      const input = form.querySelector(`[name="${name}"]`);
      if (input) dependents.push(input);
    }
  });
  return dependents;
}

// Run field validation
function runFieldValidation(el, form, opts) {
  const name = el.getAttribute('name');
  if (name) {
    validateInput(
      el,
      opts.rules[name] ?? [],
      form,
      true,
      opts.customValidators
    );
    getDependentFields(form, opts, name).forEach((input) => {
      validateInput(
        input,
        opts.rules[input.getAttribute('name')] ?? [],
        form,
        true,
        opts.customValidators
      );
    });
  }
  validateForm(null, form, opts);
}
